import React, { useReducer, useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    DialogActions,
    Button,
    Alert,
    AlertTitle,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress
} from '@mui/material';
import SelectMulti from '../../../../components/select/SelectMulti';
import MonthPickerComponent from '../../../../components/date/MonthPickerComponent';
import {toast} from 'react-toastify';
import axios from "axios";
import { FINANCIAL_PLATFORMS } from '../../../../constants/financialPlatforms';


const initialState = {
    month: '',
    platform: '',
    advertiser: '',
    sys_revenue: '',
    gross_revenue: '',
    fin_revenue: '',
    final_cost: '',
    file_link: '',
    file: null,
    comment: '',
    cost: '',
    deduction: '',
    id: '',
    confirmationOpen: false,
};

function getInitialState(editRow) {
    return {
        ...initialState,
        platform: editRow.platform || '',
        advertiser: editRow.advertiserOriginal || '',
        sys_revenue: editRow.revenue_from_the_system || '',
        gross_revenue: editRow.gross_revenue || '',
        fin_revenue: editRow.final_revenue || '',
        file_link: editRow.file || '',
        comment: editRow.revision_comment || '',
        cost: editRow.cost || '',
        deduction: editRow.deduction || '',
        final_cost: editRow.final_cost || '',
        id: editRow.id || '',
    };
}

function reducer(state, action) {
    switch (action.type) {
        case 'set':
            return { ...state, [action.field]: action.value };
        case 'toggleConfirmation':
            return { ...state, confirmationOpen: !state.confirmationOpen };
        case 'reset':
            return initialState;
        default:
            return state;
    }
}


function EditRowDialog({
                           open,
                           handleClose,
                           dsp,
                           editRow,
                           onSubmitSuccess,
                       }) {
    const [state, dispatch] = useReducer(reducer, getInitialState(editRow));

    useEffect(() => {
        dispatch({ type: 'set', field: 'platform', value: editRow.platform });
        dispatch({ type: 'set', field: 'advertiser', value: editRow.advertiserOriginal });
        dispatch({ type: 'set', field: 'sys_revenue', value: editRow.revenue_from_the_system });
        dispatch({ type: 'set', field: 'gross_revenue', value: editRow.gross_revenue });
        dispatch({ type: 'set', field: 'fin_revenue', value: editRow.final_revenue });
        dispatch({ type: 'set', field: 'file_link', value: editRow.file });
        dispatch({ type: 'set', field: 'comment', value: editRow.revision_comment });
        dispatch({ type: 'set', field: 'cost', value: editRow.cost });
        dispatch({ type: 'set', field: 'deduction', value: editRow.deduction });
        dispatch({ type: 'set', field: 'final_cost', value: editRow.final_cost });
        dispatch({ type: 'set', field: 'id', value: editRow.id });
        dispatch({ type: 'set', field: 'month', value: editRow.period });
    }, [editRow]);


    const [endpoint, setEndpoint] = useState('/api/financialtools/totalrevenue/edit');
    const BASE_URL = process.env.REACT_APP_BASE_URL;
    const handleMonthChange = (selectedMonth) => {
        const periodString = `${selectedMonth.year}-${selectedMonth.month}`;
        dispatch({ type: 'set', field: 'month', value: periodString });
    };
    const handleInputChange = (event) => {
        dispatch({ type: 'set', field: event.target.name, value: event.target.value });
    };

    const handleInputFileChange = (event) => {
        dispatch({ type: 'set', field: event.target.name, value: event.target.files[0] });
    };

    const validateFormData = () => {
        const requiredFields = [state.platform, state.advertiser, state.sys_revenue, state.gross_revenue, state.cost];
        return requiredFields.every(field => field !== '' || field === 0);
    };

    const sendToServer = async () => {
        if (!validateFormData()) {
            toast.error('Fill required fields first!');
            return;
        }

        const difference = Math.abs(state.sys_revenue - state.gross_revenue);
        const threshold = 0.5 * Math.max(state.sys_revenue, state.gross_revenue);

        if (difference > threshold) {
            dispatch({ type: 'toggleConfirmation' });
            return;
        }

        await submitData();
    };

    const submitData = async () => {
        try {
            const userId = localStorage.getItem('user_id');
            const formData = new FormData();
            const formFields = {
                file: state.file,
                user_id: userId,
                platform: state.platform,
                advertiser: state.advertiser,
                sys_revenue: state.sys_revenue,
                gross_revenue: state.gross_revenue,
                fin_revenue: state.fin_revenue,
                file_link: state.file_link,
                month: state.month,
                cost: state.cost,
                comment: state.comment,
                id: state.id
            };

            Object.keys(formFields).forEach(key => {
                if (formFields[key]) formData.append(key, formFields[key]);
            });

            const response = await axios.post(`${process.env.REACT_APP_BASE_URL}${endpoint}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            if (response.data.success) {
                toast.success(response.data.message);
                onSubmitSuccess();
                dispatch({ type: 'reset' });
                handleClose();
            } else {
                toast.error('Error: ' + response.data.message);
            }
        } catch (error) {
            toast.error(`An error occurred while sending data: ${error.message}`);
            console.error('An error occurred while sending data:', error);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose}>
            <DialogTitle>Edit {editRow.publisherDisplay}</DialogTitle>
            <DialogContent>
                <Alert variant="outlined" severity="info" color="info" sx={{marginBottom: "20px"}}>
                    Please provide the new values in this form for updating.<br/>
                </Alert>

                <div
                    style={{
                        userSelect: 'none',
                        borderRadius: '5px',
                        textAlign: "center",
                        backgroundColor: '#f0f3f5',
                        padding: '2px',
                        cursor: 'pointer',
                        border: '1px solid #dddfe1',
                    }}
                >
                    <div>
                        <InputLabel id="advertiser-label">Choose a period</InputLabel>
                    </div>
                    <div>
                        <MonthPickerComponent onMonthChange={handleMonthChange} initialMonthYear={editRow.period}/>
                    </div>
                </div>

                <FormControl fullWidth margin="dense">
                    <InputLabel id="platform-label">Platform</InputLabel>
                    <Select
                        labelId="platform-label"
                        id="platform"
                        name="platform"
                        value={(state.platform ?? editRow.platform ?? "").toLowerCase()}
                        onChange={handleInputChange}
                        required={true}
                        label="Platform"
                    >
                        {FINANCIAL_PLATFORMS.map((p) => (
                            <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl fullWidth margin="dense">
                    <InputLabel id="advertiser-label">Advertiser</InputLabel>
                    <Select
                        labelId="advertiser-label"
                        id="advertiser"
                        name="advertiser"
                        value={state.advertiser ?? editRow.advertiserOriginal}
                        onChange={handleInputChange}
                        required={true}
                        label="Advertiser"
                    >
                        {dsp.map((dsp) => (
                            <MenuItem key={dsp.value.id} value={dsp.value.id}>
                                {dsp.value.name} (ID: {dsp.value.id})
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    margin="dense"
                    id="cost"
                    label="Cost"
                    type="number"
                    fullWidth
                    name="cost"
                    value={state.cost}
                    onChange={handleInputChange}
                    required={true}
                />

                <TextField
                    margin="dense"
                    id="sys_revenue"
                    label="Revenue From The System"
                    type="number"
                    fullWidth
                    name="sys_revenue"
                    value={state.sys_revenue}
                    onChange={handleInputChange}
                    required={true}
                />

                <TextField
                    margin="dense"
                    id="gross_revenue"
                    label="Gross Revenue"
                    type="number"
                    fullWidth
                    name="gross_revenue"
                    value={state.gross_revenue}
                    onChange={handleInputChange}
                    required={true}
                />

                <TextField
                    margin="dense"
                    id="deduction"
                    label="Deduction $"
                    type="number"
                    fullWidth
                    name="deduction"
                    value={state.deduction}
                    disabled={true}
                    InputProps={{
                        readOnly: true,
                    }}
                />

                <TextField
                    margin="dense"
                    id="fin_revenue"
                    label="Final Revenue"
                    type="number"
                    fullWidth
                    name="fin_revenue"
                    value={state.fin_revenue}
                    disabled={true}
                    InputProps={{
                        readOnly: true,
                    }}
                />

                <TextField
                    margin="dense"
                    id="final_cost"
                    label="Final Cost"
                    type="number"
                    fullWidth
                    name="final_cost"
                    value={state.final_cost}
                    disabled={true}
                    InputProps={{
                        readOnly: true,
                    }}
                />

                <TextField
                    margin="dense"
                    id="file_link"
                    label="File Link"
                    type="text"
                    fullWidth
                    name="file_link"
                    value={state.file_link}
                    onChange={handleInputChange}
                />

                <div>
                    <InputLabel id="advertiser-label">Or choose a file</InputLabel>
                </div>
                <div>
                    <input
                        type="file"
                        id="file"
                        name="file"
                        onChange={handleInputFileChange}
                    />
                </div>

                <TextField
                    margin="dense"
                    id="comment"
                    label="Revision Comment"
                    type="text"
                    fullWidth
                    name="comment"
                    value={state.comment}
                    onChange={handleInputChange}
                    multiline
                    rows={3}
                />

                {editRow.final_number_billing && (() => {
                    const formatBillingInfo = (text) => {
                        if (!text) return null;
                        
                        const lines = text.split('\n');
                        const formulaLine = lines.find(line => line.startsWith('Formula:'));
                        const calculationLines = lines.filter(line => 
                            line.includes(':') && 
                            !line.startsWith('Formula:') && 
                            !line.startsWith('Deduction Records:') &&
                            !line.startsWith('-') &&
                            !line.startsWith('  ') &&
                            line.trim() !== '' &&
                            line !== 'Calculation:'
                        );
                        
                        // Отделяем Final Cost/Revenue от остальных полей
                        const finalField = calculationLines.find(line => 
                            line.toLowerCase().includes('final cost') || line.toLowerCase().includes('final revenue')
                        );
                        const otherFields = calculationLines.filter(line => 
                            !line.toLowerCase().includes('final cost') && !line.toLowerCase().includes('final revenue')
                        );
                        
                        const deductionStartIndex = lines.findIndex(line => line.startsWith('Deduction Records:'));
                        const deductionLines = deductionStartIndex >= 0 ? lines.slice(deductionStartIndex) : [];
                        
                        // Парсим Deduction Records для форматирования
                        const formattedDeductionRecords = [];
                        let currentRecord = null;
                        
                        deductionLines.forEach((line, index) => {
                            if (line.startsWith('Deduction Records:') || line.trim() === '') {
                                formattedDeductionRecords.push({ type: 'text', content: line });
                            } else if (line.startsWith('-')) {
                                // Начало новой записи
                                if (currentRecord) {
                                    formattedDeductionRecords.push(currentRecord);
                                }
                                currentRecord = {
                                    type: 'deduction',
                                    periodReason: line,
                                    publisher: null,
                                    advertiser: null,
                                    deduction: null
                                };
                            } else if (line.startsWith('  Deduction:')) {
                                // Значение Deduction - последнее в записи
                                const deductionMatch = line.match(/Deduction:\s*(.+)/);
                                if (deductionMatch && currentRecord) {
                                    currentRecord.deduction = deductionMatch[1].trim();
                                }
                            } else if (line.startsWith('  Publisher:')) {
                                if (currentRecord) {
                                    currentRecord.publisher = line;
                                }
                            } else if (line.startsWith('  Advertiser:')) {
                                if (currentRecord) {
                                    currentRecord.advertiser = line;
                                }
                            } else {
                                formattedDeductionRecords.push({ type: 'text', content: line });
                            }
                        });
                        
                        if (currentRecord) {
                            formattedDeductionRecords.push(currentRecord);
                        }
                        
                        return {
                            formula: formulaLine || '',
                            fields: otherFields.map(line => {
                                const colonIndex = line.indexOf(':');
                                if (colonIndex > 0) {
                                    const label = line.substring(0, colonIndex).trim();
                                    const value = line.substring(colonIndex + 1).trim();
                                    return { label, value };
                                }
                                return { label: line, value: '' };
                            }),
                            finalField: finalField ? (() => {
                                const colonIndex = finalField.indexOf(':');
                                if (colonIndex > 0) {
                                    const label = finalField.substring(0, colonIndex).trim();
                                    const value = finalField.substring(colonIndex + 1).trim();
                                    return { label, value };
                                }
                                return { label: finalField, value: '' };
                            })() : null,
                            deductionRecords: formattedDeductionRecords
                        };
                    };
                    
                    const formatted = formatBillingInfo(editRow.final_number_billing);
                    if (!formatted) return null;
                    
                    return (
                        <Alert 
                            severity="info"
                            sx={{
                                mt: 2,
                                '& .MuiAlert-message': {
                                    width: '100%',
                                }
                            }}
                        >
                            <AlertTitle>Billing Information</AlertTitle>
                            <div style={{ padding: '8px 0' }}>
                                {formatted.formula && (
                                    <div style={{ marginBottom: '20px', fontWeight: 'bold', fontSize: '1rem' }}>
                                        {formatted.formula}
                                    </div>
                                )}
                                
                                {formatted.fields.length > 0 && (
                                    <div style={{ marginBottom: '20px', maxWidth: '600px' }}>
                                        {formatted.fields.map((field, index) => (
                                            <div 
                                                key={index} 
                                                style={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center',
                                                    gap: '16px',
                                                    marginBottom: '8px',
                                                    padding: '4px 0'
                                                }}
                                            >
                                                <span>{field.label}:</span>
                                                <span style={{ marginLeft: 'auto', fontWeight: '500' }}>
                                                    {field.value}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                
                                {formatted.deductionRecords.length > 0 && (
                                    <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                                        {formatted.deductionRecords.map((record, index) => {
                                            if (record.type === 'deduction') {
                                                return (
                                                    <div key={index} style={{ marginBottom: '12px' }}>
                                                        <div style={{ marginBottom: '4px' }}>{record.periodReason}</div>
                                                        {record.publisher && (
                                                            <div style={{ marginBottom: '4px' }}>{record.publisher}</div>
                                                        )}
                                                        {record.advertiser && (
                                                            <div style={{ marginBottom: '4px' }}>{record.advertiser}</div>
                                                        )}
                                                        {record.deduction && (
                                                            <div style={{ 
                                                                display: 'flex', 
                                                                alignItems: 'center',
                                                                gap: '16px',
                                                                marginBottom: '4px'
                                                            }}>
                                                                <span>  Deduction:</span>
                                                                <span style={{ 
                                                                    marginLeft: 'auto', 
                                                                    color: '#d32f2f',
                                                                    fontWeight: '500'
                                                                }}>
                                                                    {record.deduction}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            } else {
                                                return (
                                                    <div key={index} style={{ whiteSpace: 'pre-line', marginBottom: '4px' }}>
                                                        {record.content}
                                                    </div>
                                                );
                                            }
                                        })}
                                    </div>
                                )}
                                
                                {formatted.finalField && (
                                    <div style={{ marginTop: '20px', maxWidth: '600px', borderTop: '1px solid #e0e0e0', paddingTop: '16px' }}>
                                        <div style={{ 
                                            display: 'flex', 
                                            alignItems: 'center',
                                            gap: '16px'
                                        }}>
                                            <span>{formatted.finalField.label}:</span>
                                            <span style={{ marginLeft: 'auto', fontWeight: '500' }}>
                                                {formatted.finalField.value}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Alert>
                    );
                })()}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="primary">
                    Cancel
                </Button>
                <Button onClick={sendToServer} color="success">
                    Save
                </Button>
            </DialogActions>

            <Dialog
                open={state.confirmationOpen}
                onClose={() => dispatch({ type: 'toggleConfirmation' })}
            >
                <DialogTitle>Confirmation Required</DialogTitle>
                <DialogContent>
                    The difference between System Revenue and Gross Revenue is more than 50%. Are you sure you want to proceed?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => dispatch({ type: 'toggleConfirmation' })} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={submitData} color="success">
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
}

export default EditRowDialog;